import { PHYSICS_DOMAINS_ENABLED } from './physicsDomains/domainTypes';
import {
  Atom,
  AudioWaveform,
  BarChart3,
  BatteryCharging,
  Bird,
  Calculator,
  Droplets,
  Flower,
  GlassWater,
  HeartPulse,
  ListTree,
  FlaskConical,
  Gauge,
  GitFork,
  Dna,
  Microscope,
  GitBranch,
  Layers3,
  Leaf,
  Mountain,
  Plug,
  Radiation,
  Scale,
  Scissors,
  ShieldPlus,
  ScanSearch,
  Shapes,
  Sigma,
  Sparkles,
  Sprout,
  Sun,
  Split,
  Telescope,
  Thermometer,
  Trees,
  TrendingUp,
  Wind,
} from 'lucide-react';

import type { StudyToolCategory } from './studyToolTypes';

/** Topic groups per subject, in display order (split out of studyTools.ts, SIR-HYG-07). */
export const STUDY_TOOL_CATEGORIES: StudyToolCategory[] = [
  ...(PHYSICS_DOMAINS_ENABLED
    ? [
        {
          id: 'senior-experiments',
          subjectKey: 'physics',
          name: 'Further physics experiments',
          description:
            'Preview heat, momentum, electromagnetism, gravity and modern physics models.',
          icon: Atom,
        },
      ]
    : []),

  {
    id: 'number-finance',
    subjectKey: 'mathematics',
    name: 'Number, powers & finance',
    description: 'Connect fractions, indices, percentage change and money models.',
    icon: Calculator,
  },
  {
    id: 'algebra-functions',
    subjectKey: 'mathematics',
    name: 'Algebra & functions',
    description: 'See equations, rules, tables and graphs as connected representations.',
    icon: Sigma,
  },
  {
    id: 'geometry-coordinate',
    subjectKey: 'mathematics',
    name: 'Geometry, coordinates & trigonometry',
    description: 'Measure shapes and reason with coordinates, distance and triangles.',
    icon: Shapes,
  },
  {
    id: 'patterns-change',
    subjectKey: 'mathematics',
    name: 'Patterns, growth & change',
    description: 'Explore sequences, exponential growth and rates of change.',
    icon: TrendingUp,
  },
  {
    id: 'data-chance',
    subjectKey: 'mathematics',
    name: 'Data & chance',
    description: 'Compare data summaries, displays and simulated probability.',
    icon: BarChart3,
  },
  {
    id: 'matter-foundations',
    subjectKey: 'chemistry',
    name: 'Matter & change',
    description: 'From everyday changes to particles, mixtures and reaction evidence.',
    icon: Layers3,
  },
  {
    id: 'elements',
    subjectKey: 'chemistry',
    name: 'Elements & atoms',
    description: 'What everything is made of.',
    icon: Atom,
  },
  {
    id: 'structure-bonding',
    subjectKey: 'chemistry',
    name: 'Structure & bonding',
    description: 'How valence electrons become bonds, shapes and polarity.',
    icon: Sparkles,
  },
  {
    id: 'reactions',
    subjectKey: 'chemistry',
    name: 'Reactions & equations',
    description: 'How substances change and how to balance them.',
    icon: FlaskConical,
  },
  {
    id: 'energy-thermo',
    subjectKey: 'chemistry',
    name: 'Energy & thermochemistry',
    description: 'Where reaction energy goes, how to measure it and how to compare fuels.',
    icon: Thermometer,
  },
  {
    id: 'gases-quantities',
    subjectKey: 'chemistry',
    name: 'Gases & quantities',
    description: 'How pressure, volume, temperature and amount of gas are related.',
    icon: Wind,
  },
  {
    id: 'water-materials',
    subjectKey: 'chemistry',
    name: 'Water & materials',
    description: 'How water behaves and how metals and materials are made and chosen.',
    icon: GlassWater,
  },
  {
    id: 'rates-systems',
    subjectKey: 'chemistry',
    name: 'Rates & systems',
    description: 'Why reaction speed changes and how reacting systems develop over time.',
    icon: Gauge,
  },
  {
    id: 'acids-ions',
    subjectKey: 'chemistry',
    name: 'Acids, ions & electrochemistry',
    description: 'How ions transfer, react and change pH or charge.',
    icon: Droplets,
  },
  {
    id: 'organic-analysis',
    subjectKey: 'chemistry',
    name: 'Organic chemistry',
    description: 'How carbon chains, functional groups, names and pathways fit together.',
    icon: GitBranch,
  },
  {
    id: 'analysis-separation',
    subjectKey: 'chemistry',
    name: 'Analysis & separation',
    description: 'How evidence from separated components and instrument traces identifies matter.',
    icon: ScanSearch,
  },
  {
    id: 'motion-forces',
    subjectKey: 'physics',
    name: 'Motion & forces',
    description: 'How things move, and the pushes and pulls that change their motion.',
    icon: Gauge,
  },
  {
    id: 'energy',
    subjectKey: 'physics',
    name: 'Energy',
    description: 'Stored, moving and heat energy, and why the total stays the same.',
    icon: BatteryCharging,
  },
  {
    id: 'electricity',
    subjectKey: 'physics',
    name: 'Electricity',
    description: 'Circuits, current, voltage and resistance.',
    icon: Plug,
  },
  {
    id: 'waves',
    subjectKey: 'physics',
    name: 'Waves, sound & light',
    description: 'How waves carry energy, how v = fλ links them, and how light bends and reflects.',
    icon: AudioWaveform,
  },
  {
    id: 'atoms',
    subjectKey: 'physics',
    name: 'Atoms & radiation',
    description: 'Unstable atoms, radioactive decay and half-life.',
    icon: Radiation,
  },
  {
    id: 'cells',
    subjectKey: 'biology',
    name: 'Cells',
    description: 'What living things are made of: animal and plant cells and their parts.',
    icon: Microscope,
  },
  {
    id: 'membranes',
    subjectKey: 'biology',
    name: 'Cell membranes',
    description:
      'How substances cross the cell membrane: diffusion, osmosis and transport proteins.',
    icon: Droplets,
  },
  {
    id: 'energy',
    subjectKey: 'biology',
    name: 'Photosynthesis & respiration',
    description:
      'How cells capture and release energy, and how carbon cycles between plants, animals and the air.',
    icon: Sun,
  },
  {
    id: 'enzymes',
    subjectKey: 'biology',
    name: 'Enzymes',
    description: 'How enzymes speed up reactions, and what makes them work faster, slower or stop.',
    icon: FlaskConical,
  },
  {
    id: 'immunity',
    subjectKey: 'biology',
    name: 'Disease & immunity',
    description:
      'How the body recognises and fights pathogens, and how vaccines protect people and communities.',
    icon: ShieldPlus,
  },
  {
    id: 'evolution',
    subjectKey: 'biology',
    name: 'Evolution',
    description:
      'How populations change over generations: natural selection, genetic drift and evolutionary trees.',
    icon: Bird,
  },
  {
    id: 'biotechnology',
    subjectKey: 'biology',
    name: 'Biotechnology',
    description:
      'How scientists copy, sort, cut and edit DNA: PCR, gel electrophoresis, recombinant DNA and CRISPR.',
    icon: Scissors,
  },
  {
    id: 'reproduction',
    subjectKey: 'biology',
    name: 'Reproduction',
    description:
      'How living things reproduce: asexual and sexual reproduction, sperm and eggs, fertilisation and flowers.',
    icon: Flower,
  },
  {
    id: 'plant-growth',
    subjectKey: 'biology',
    name: 'Plant growth',
    description:
      'What plants need to grow and how animals choose where to live, tested with fair tests.',
    icon: Sprout,
  },
  {
    id: 'ecosystems',
    subjectKey: 'biology',
    name: 'Ecosystems',
    description:
      'Food webs, energy through food chains, how predator and prey numbers rise and fall, and how ecologists sample populations.',
    icon: Trees,
  },
  {
    id: 'plant-transport',
    subjectKey: 'biology',
    name: 'Plant transport',
    description:
      'How water moves up through a plant and out of its leaves, and how sugars travel in the phloem.',
    icon: Leaf,
  },
  {
    id: 'body-systems',
    subjectKey: 'biology',
    name: 'Body systems',
    description: 'Organs working together, and how the body keeps glucose and temperature steady.',
    icon: HeartPulse,
  },
  {
    id: 'classification',
    subjectKey: 'biology',
    name: 'Classification',
    description: 'Dichotomous keys and how living things are grouped from kingdom to species.',
    icon: ListTree,
  },
  {
    id: 'cell-division',
    subjectKey: 'biology',
    name: 'Cell division',
    description: 'How cells copy themselves (mitosis) and make sperm and egg cells (meiosis).',
    icon: Split,
  },
  {
    id: 'inheritance',
    subjectKey: 'biology',
    name: 'Genetics & inheritance',
    description:
      'How traits pass from parents to children: Punnett squares, chance and family pedigrees.',
    icon: GitFork,
  },
  {
    id: 'dna-proteins',
    subjectKey: 'biology',
    name: 'DNA & proteins',
    description: 'How a cell reads a gene and builds a protein.',
    icon: Dna,
  },
  {
    id: 'toolkit',
    subjectKey: 'physics',
    name: 'Formulas & units',
    description: 'Solve formulas step by step, change units and look up constants.',
    icon: Calculator,
  },
  {
    id: 'earth-space',
    subjectKey: 'science',
    name: 'Earth in space',
    description: "Explore seasons, Moon phases, eclipses and Earth's energy balance.",
    icon: Telescope,
  },
  {
    id: 'dynamic-earth',
    subjectKey: 'science',
    name: 'Dynamic Earth',
    description: 'Plate boundaries, rocks, minerals and evidence of a changing planet.',
    icon: Mountain,
  },
  {
    id: 'earth-systems',
    subjectKey: 'science',
    name: 'Earth systems',
    description: 'Carbon moving between connected parts of the Earth system.',
    icon: Trees,
  },
  {
    id: 'weather-climate',
    subjectKey: 'science',
    name: 'Weather & atmosphere',
    description: 'Use observations as evidence without treating a pattern as a certain forecast.',
    icon: Gauge,
  },
  {
    id: 'water-systems',
    subjectKey: 'science',
    name: 'Water systems',
    description: 'Trace water through catchments, surfaces, groundwater and oceans.',
    icon: Droplets,
  },
  {
    id: 'earth-data',
    subjectKey: 'science',
    name: 'Earth data & fieldwork',
    description: 'Read satellite pixels, maps, cross-sections and field observations as evidence.',
    icon: BarChart3,
  },
  {
    id: 'resources-sustainability',
    subjectKey: 'science',
    name: 'Resources & sustainability',
    description: 'Account for finite stocks, recovery, regeneration and environmental limits.',
    icon: Scale,
  },
];
